/**
 * File I/O utilities for persisting rate limiter state
 */

import * as fs from 'fs';
import * as path from 'path';
import { fileURLToPath } from 'url';

/* ------------------------------------------------------------------ */
/* ESM-safe dirname / filename                                         */
/* ------------------------------------------------------------------ */

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

/* ------------------------------------------------------------------ */
/* Directory helpers                                                   */
/* ------------------------------------------------------------------ */

export function createDir(dirPath: string): string {
  if (!fs.existsSync(dirPath)) {
    fs.mkdirSync(dirPath, { recursive: true });
  }
  return dirPath;
}

export function getAbsFile(): string {
  return __filename;
}

export function getAbsDir(): string {
  return __dirname;
}

export function getAbsPath(relativePath: string): string {
  return path.join(getAbsDir(), relativePath);
}

/* ------------------------------------------------------------------ */
/* Rate limiter persistence paths                                      */
/* ------------------------------------------------------------------ */

export function getRateLimitHistoryDir(): string {
  const rateLimitHistoryDir = getAbsPath('rateLimitHistory');
  createDir(rateLimitHistoryDir);
  return rateLimitHistoryDir;
}

export function getSaveStatePath(): string {
  return path.join(getRateLimitHistoryDir(), 'rate_limiter_state.json');
}

export function getRateLimitsPath(): string {
  return path.join(getRateLimitHistoryDir(), 'rate_limits.json');
}

/* ------------------------------------------------------------------ */
/* Safe JSON I/O                                                       */
/* ------------------------------------------------------------------ */

export function safeDumpToFile(data: unknown, filePath: string): void {
  try {
    createDir(path.dirname(filePath));

    const tmpPath = `${filePath}.tmp`;
    fs.writeFileSync(tmpPath, JSON.stringify(data, null, 2), 'utf-8');

    // atomic replace
    fs.renameSync(tmpPath, filePath);
  } catch (error) {
    console.error(`Failed to write to ${filePath}:`, error);
  }
}

export function safeLoadFromJson<T = any>(filePath: string): T | null {
  try {
    if (!fs.existsSync(filePath)) return null;
    const raw = fs.readFileSync(filePath, 'utf-8').trim();
    return JSON.parse(raw);
  } catch (error) {
    console.error(`Corrupt JSON detected: ${filePath}`, error);

    // rename bad file for inspection
    try {
      fs.renameSync(
        filePath,
        `${filePath}.corrupt.${Date.now()}`
      );
    } catch {}

    return null;
  }
}


/* ------------------------------------------------------------------ */
/* Initializers                                                        */
/* ------------------------------------------------------------------ */

export function readLimits(
  url1: string,
  url2: string,
  filePath?: string
): any {
  const targetPath = filePath || getRateLimitsPath();

  if (!fs.existsSync(targetPath)) {
    safeDumpToFile({ [url1]: {}, [url2]: {} }, targetPath);
  }

  return safeLoadFromJson(targetPath);
}

export function readSaveState(
  url1: string,
  url2: string,
  filePath?: string
): any {
  const targetPath = filePath || getSaveStatePath();

  if (!fs.existsSync(targetPath)) {
    safeDumpToFile(
      {
        last_method: null,
        rate_limits: { [url1]: [], [url2]: [] },
        last_mb: { [url1]: {}, [url2]: {} },
        cooldown_times: { [url1]: {}, [url2]: {} },
        last_url: url1
      },
      targetPath
    );
  }

  return safeLoadFromJson(targetPath);
}
